import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Clock, ArrowRight, MessageSquare, User } from "lucide-react";
import { useState } from "react";
import { format } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { statusDisplayMap, statusOptions, getStatusColor, getStatusIcon } from "@/constants/proposalMeta";

interface ProposalStatusWorkflowProps {
  proposalId: string;
  currentStatus: string;
  onStatusChange: (newStatus: string) => void;
  isReadonly?: boolean;
}

const statusWorkflow = {
  draft: ["sent", "rejected", "cancelled"],
  sent: ["pending", "accepted", "rejected", "cancelled"],
  pending: ["accepted", "rejected", "cancelled", "sent"],
  accepted: ["sent", "cancelled"], // Can be re-sent if needed
  rejected: ["draft", "sent", "cancelled"], // Can be revised and re-sent
  cancelled: ["draft", "sent"], // Can be reactivated
};

export function ProposalStatusWorkflow({ 
  proposalId, 
  currentStatus, 
  onStatusChange, 
  isReadonly = false 
}: ProposalStatusWorkflowProps) {
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [notes, setNotes] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch status history
  const { data: statusHistory } = useQuery({
    queryKey: ["proposal-status-history", proposalId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proposal_status_history")
        .select("*")
        .eq("proposal_id", proposalId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!proposalId,
  });

  // Change status mutation
  const changeStatusMutation = useMutation({
    mutationFn: async ({ status, notes }: { status: string; notes?: string }) => {
      // Update proposal status
      const { error: updateError } = await supabase
        .from("proposals")
        .update({ status })
        .eq("id", proposalId);

      if (updateError) throw updateError;

      // Add notes to history if provided
      if (notes) {
        const { error: historyError } = await supabase
          .from("proposal_status_history")
          .update({ notes })
          .eq("proposal_id", proposalId)
          .eq("to_status", status)
          .eq("created_at", (await supabase
            .from("proposal_status_history")
            .select("created_at")
            .eq("proposal_id", proposalId)
            .eq("to_status", status)
            .order("created_at", { ascending: false })
            .limit(1)
            .single()
          ).data?.created_at);

        if (historyError) console.warn("Failed to add notes to history:", historyError);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal-status-history", proposalId] });
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      setIsChangingStatus(false);
      setNewStatus("");
      setNotes("");
      toast({
        title: "Success",
        description: "Proposal status updated successfully",
      });
      onStatusChange(newStatus);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update status",
        variant: "destructive",
      });
    },
  });

  const handleStatusChange = () => {
    if (!newStatus) return;
    changeStatusMutation.mutate({ status: newStatus, notes });
  };

  const availableStatuses = statusWorkflow[currentStatus as keyof typeof statusWorkflow] || [];
  const CurrentStatusIcon = getStatusIcon(currentStatus);

  const getStatusWorkflowLogic = (status: string) => {
    switch (status) {
      case "sent":
        return "Proposal has been sent to client and is awaiting response";
      case "pending":
        return "Client is reviewing the proposal";
      case "accepted":
        return "Client has accepted the proposal - ready to convert to project";
      case "rejected":
        return "Client has rejected the proposal - can be revised and re-sent";
      case "cancelled":
        return "Proposal has been cancelled and is no longer active";
      default:
        return "Select next status based on current situation";
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Status & Quick Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Status & Workflow</CardTitle>
            {!isReadonly && availableStatuses.length > 0 && (
              <Dialog open={isChangingStatus} onOpenChange={setIsChangingStatus}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Change Status
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Update Proposal Status</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Current Status</Label>
                      <div className="mt-1">
                        <Badge className={getStatusColor(currentStatus)}>
                          <CurrentStatusIcon className="h-3 w-3 mr-1" />
                          {statusDisplayMap[currentStatus as keyof typeof statusDisplayMap]}
                        </Badge>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="new-status">New Status</Label>
                      <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select new status" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableStatuses.map((status) => {
                            const StatusIcon = getStatusIcon(status);
                            return (
                              <SelectItem key={status} value={status}>
                                <div className="flex items-center">
                                  <StatusIcon className="h-4 w-4 mr-2" />
                                  {statusDisplayMap[status as keyof typeof statusDisplayMap]}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {newStatus && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {getStatusWorkflowLogic(newStatus)}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="notes">Notes (Optional)</Label>
                      <Textarea
                        id="notes"
                        placeholder="Add any notes about this status change..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsChangingStatus(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleStatusChange}
                        disabled={!newStatus || changeStatusMutation.isPending}
                      >
                        {changeStatusMutation.isPending ? "Updating..." : "Update Status"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <Badge className={getStatusColor(currentStatus)} variant="outline">
              <CurrentStatusIcon className="h-4 w-4 mr-2" />
              {statusDisplayMap[currentStatus as keyof typeof statusDisplayMap]}
            </Badge>
            <p className="text-sm text-muted-foreground">
              {getStatusWorkflowLogic(currentStatus)}
            </p>
          </div>

          {availableStatuses.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-muted-foreground mb-2">Available Transitions:</p>
              <div className="flex flex-wrap gap-2">
                {availableStatuses.map((status) => {
                  const StatusIcon = getStatusIcon(status);
                  return (
                    <Badge key={status} variant="secondary" className="text-xs">
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusDisplayMap[status as keyof typeof statusDisplayMap]}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status History Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Status History</CardTitle>
        </CardHeader>
        <CardContent>
          {statusHistory && statusHistory.length > 0 ? (
            <div className="space-y-4">
              {statusHistory.map((entry, index) => {
                const ToStatusIcon = getStatusIcon(entry.to_status);
                const FromStatusIcon = entry.from_status ? getStatusIcon(entry.from_status) : null;
                
                return (
                  <div key={entry.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <ToStatusIcon className="h-4 w-4 text-primary" />
                      </div>
                      {index < statusHistory.length - 1 && (
                        <div className="w-0.5 h-6 bg-border mx-auto mt-2" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <Badge className={getStatusColor(entry.to_status)} variant="outline">
                          <ToStatusIcon className="h-3 w-3 mr-1" />
                          {statusDisplayMap[entry.to_status as keyof typeof statusDisplayMap]}
                        </Badge>
                        {entry.from_status && FromStatusIcon && (
                          <>
                            <span className="text-muted-foreground text-sm">from</span>
                            <Badge variant="secondary">
                              <FromStatusIcon className="h-3 w-3 mr-1" />
                              {statusDisplayMap[entry.from_status as keyof typeof statusDisplayMap]}
                            </Badge>
                          </>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2 mt-1 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{format(new Date(entry.created_at), "MMM dd, yyyy 'at' HH:mm")}</span>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          <User className="h-3 w-3" />
                          <span>User</span>
                        </div>
                      </div>
                      
                      {entry.notes && (
                        <div className="mt-2 p-2 bg-muted/50 rounded text-sm flex items-start space-x-2">
                          <MessageSquare className="h-3 w-3 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <span>{entry.notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              <Clock className="h-6 w-6 mx-auto mb-2 opacity-50" />
              <p>No status changes recorded yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}